import { PaginationResp } from '.'
import apiRequest from '../config/api'

export interface Course {
  poster: string
  student_count: string
  questions_count: string
  end_date: string
  start_date: string
  id: number
  title: string
  category_id: string
  course_level: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCE' | 'EXPERT'
  max_student: string
  is_public: boolean
  short_description: string
  description: string
  image: string
  link_ebook: string
  link_group: string
  slug: string[]
  price: string
  discount_type: 'PERCENTAGE' | 'NOMINAL'
  discount: string
  created_at: string
  updated_at: string
  category: Category
  topics: Topic[]
  cross_sells: any[]
  benefits: Benefit[]
  studied: string[]
  type?: string
  is_bought?: boolean
  is_answered?: boolean
}

export interface Category {
  id: number
  name: string
  created_at: string
  updated_at: string
}

export interface Topic {
  id: number
  course_id: string
  name: string
  created_at: string
  updated_at: string
  lessons: Lesson[]
}

export interface Lesson {
  id: number
  topic_id: string
  name: string
  video_link: string
  duration: string
  description: string
  is_premium: boolean
  created_at: string
  updated_at: string
  is_watched?: boolean
}

export interface Benefit {
  id: number
  icon: string
  name: string
  created_at: string
  updated_at: string
  pivot: BenefitPivot
}

export interface BenefitPivot {
  course_id: string
  benefit_id: string
}

// CBT Interfaces
export interface CBTAnswer {
  id: number
  question_id: string
  choice: string
  is_true?: boolean
}

export interface CBTQuestion {
  id: number
  course_id: string
  question: string
  discussion: string
  created_at: string
  updated_at: string
  image: string
  answers: CBTAnswer[]
  normal_lab: string
}

export interface CBTData {
  id: number
  title: string
  category_id: string
  course_level: string
  max_student: string
  max_attempt: string,
  min_passing_score: string,
  is_public: boolean
  short_description: string
  description: string
  status: string
  type: string
  start_date: string | null
  end_date: string | null
  duration: string
  image: string
  poster: string
  link_ebook: string | null
  link_group: string | null
  slug: string | null
  studied: string[]
  price: string
  discount_type: string
  discount: string
  created_at: string
  updated_at: string
  certificate_downloads: {
    user_id: number
    course_id: number
    name: string
  } | null
  questions: CBTQuestion[],
  user_attempt_count: number
}

export interface CBTResponse {
  message: string
  data: CBTData
}

export interface CBTAnswerSubmission {
  question_id: number
  answer_id: number
}

export interface CBTSubmitRequest {
  course_id: number
  answers: CBTAnswerSubmission[]
}

export interface CBTSubmitResponse {
  message: string
  data: {
    score: string
    correct_count: number
    total_questions: number
    user_answers: CBTUserAnswer[]
  }
}

export interface CBTUserAnswer {
  user_id: number
  course_id: number
  question_id: number
  answer_id: number
  is_correct: boolean
  updated_at: string
  created_at: string
  id: number
}

export interface Summary {
  active: number
  draft: number
  free: number
  paid: number
  unpublished: number
}

export type CourseListResponse = {
  data: Course[]
  summary: Summary
} & PaginationResp

export type CourseDetailResponse = Course

export const getCategories = async (
  params: any,
  isAdmin?: boolean,
): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/categories' : 'user/categories'

  const res = await apiRequest.get(`/api/${url}`, {
    params: { limit: 99, page: 1, ...params },
  })

  return res.data.data
}

export const postCategories = async (
  { name, type }: { name: string, type: string },
  isAdmin?: boolean,
): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/categories' : 'user/categories'

  const res = await apiRequest.post(`/api/${url}`, { name, type })

  return res.data.data
}

export const putCategories = async (
  id: number,
  { name }: { name: string },
  isAdmin?: boolean,
): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/categories' : ''

  const res = await apiRequest.put(`/api/${url}/${id}`, { name })

  return res.data.data
}

export const deleteCategories = async (
  id: number,
  isAdmin?: boolean,
): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/categories' : ''

  const res = await apiRequest.delete(`/api/${url}/${id}`)

  return res.data.data
}

export const postCourse = async (
  formData: FormData,
  isAdmin?: boolean,
): Promise<CourseDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/courses' : 'user/courses'

  const res = await apiRequest.post(`/api/${url}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return res.data
}

export const postPublishCourse = async (
  id: number,
  isAdmin?: boolean,
): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin
    ? `admin/courses/${id}/publish`
    : `user/courses/${id}/publish`

  const res = await apiRequest.post(`/api/${url}`)

  return res.data
}

export const postUnpublishCourse = async (
  id: number,
  isAdmin?: boolean,
): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin
    ? `admin/courses/${id}/unpublish`
    : `user/courses/${id}/unpublish`

  const res = await apiRequest.post(`/api/${url}`)

  return res.data
}

export const putCourseUpdate = async (
  id: string,
  formData: FormData,
  isAdmin?: boolean,
): Promise<CourseDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/courses' : 'user/courses'

  const res = await apiRequest.post(`/api/${url}/${id}?_method=PUT`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return res.data
}

export const getCourses = async (
  params: any,
  isAdmin?: boolean,
): Promise<CourseListResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/courses' : 'user/courses'

  const res = await apiRequest.get(`/api/${url}`, {
    params: { limit: 10, page: 1, ...params },
  })

  return res.data.data
}

export const getCourseDetail = async (
  id: string,
  isAdmin?: boolean,
): Promise<CourseDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/courses' : 'user/courses'

  const res = await apiRequest.get(`/api/${url}/${id}`)

  return res.data.data
}

interface ManualTransactionResponse {
  message: string
  data?: any
}

export const createManualTransaction = async (
  payload: any,
): Promise<ManualTransactionResponse> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.post('/api/payment_manual', payload)
  return res.data
}

export const getCourseDetailLoggedIn = async (
  id: string,
): Promise<CourseDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.get(`/api/user/courses/login/${id}`)

  return res.data.data
}

export const deleteCourse = async (
  id: number,
  isAdmin?: boolean,
): Promise<CourseDetailResponse> => {
  await apiRequest.getCsrfCookie()

  const url = isAdmin ? 'admin/courses' : 'user/courses'

  const res = await apiRequest.delete(`/api/${url}/${id}`)

  return res.data.data
}

export const getCBTDetail = async (id: string): Promise<CBTResponse> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.get(`/api/user/courses/cbt/${id}`)

  return res.data
}

export const submitCBTAnswers = async (
  data: CBTSubmitRequest,
): Promise<CBTSubmitResponse> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.post(`/api/user/courses/cbt/submit-answer`, data)

  return res.data
}

// Analytics interfaces
export interface CourseAnalyticsData {
  transaction_count: string
  revenue: string
  certificate_count: number
  chart: {
    transaction: {
      monthly: {
        week_1: number
        week_2: number
        week_3: number
        week_4: number
      }
      yearly: {
        january: number
        february: number
        march: number
        april: number
        may: number
        june: number
        july: number
        august: number
        september: number
        october: number
        november: number
        december: number
      }
    }
    revenue: {
      monthly: {
        week_1: string
        week_2: string
        week_3: string
        week_4: string
      }
      yearly: {
        january: string
        february: string
        march: string
        april: string
        may: string
        june: string
        july: string
        august: string
        september: string
        october: string
        november: string
        december: string
      }
    }
  }
}

export interface CourseAnalyticsResponse {
  message: string
  data: CourseAnalyticsData
}

export const getCourseAnalytics = async (
  id: string,
): Promise<CourseAnalyticsResponse> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.get(`/api/admin/courses/analytic/${id}`)

  return res.data
}

export const dowloadCBTResult = async (
  id: string,
): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.get(`/api/admin/courses/analytic/${id}/download`)

  console.log(res);

  return res.data
}

export const markLessonAsWatched = async (lessonId: number): Promise<any> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.post(
    `/api/user/courses/lessons/${lessonId}/watch`,
  )

  return res.data
}

// CBT Answered Data Interfaces
export interface CBTAnsweredUserAnswer {
  id: number
  user_id: string
  course_id: string
  question_id: string
  answer_id: string
  is_correct: boolean
  created_at: string
  updated_at: string
}

export interface CBTAnsweredData {
  id: number
  title: string
  category_id: string
  course_level: string
  max_student: string
  is_public: boolean
  short_description: string
  description: string
  status: string
  type: string
  start_date: string | null
  end_date: string | null
  duration: string
  image: string
  is_passed: boolean
  poster: string
  link_ebook: string | null
  link_group: string | null
  slug: string | null
  studied: string[]
  price: string
  discount_type: string
  discount: string
  created_at: string
  updated_at: string
  questions: CBTQuestion[]
  user_answers: CBTAnsweredUserAnswer[]
  certificate_downloads: {
    user_id: number
    course_id: number
    name: string
  } | null
  min_passing_score: string
  score: number
}

export interface CBTAnsweredResponse {
  message: string
  data: CBTAnsweredData
}

export const getCBTAnswered = async (
  id: string,
): Promise<CBTAnsweredResponse> => {
  await apiRequest.getCsrfCookie()

  const res = await apiRequest.get(`/api/user/courses/cbt/answered/${id}`)

  return res.data
}

export const startCBT = async (data: { course_id: string, user_id: string }) => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post(`/api/user/courses/cbt/start-cbt`, data)

  return res.data
}

export const getAllCBTAttempt = async (course_id: string) => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get(`/api/user/courses/cbt/cbt-attempt/${course_id}`);

  return res.data;
}

export const flagFinishAttempt = async (data: { course_id: string }) => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post(`/api/user/courses/cbt/finish-cbt`, data)

  return res.data
}